import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, SelectProps } from '@mui/material';
import { useMemo } from 'react';
import { MenuSxPropMedium, MenuSxPropSmall, renderMultipleValue, selectBoxStyles } from './SelectBox.helpers';

export type SelectBoxProps<T> = SelectProps<T> & {
  fullWidth?: boolean;
  helperText?: string;
  allowEmpty?: boolean;
  showNotMapped?: boolean;
};

export const SelectBox = <T,>(props: SelectBoxProps<T>): JSX.Element => {
  const {
    label = '',
    placeholder = '',
    children,
    multiple = false,
    fullWidth = true,
    displayEmpty = true,
    helperText,
    error,
    sx,
    value = '',
    size = 'medium',
    allowEmpty = false,
    renderValue,
    MenuProps = {},
    showNotMapped = true,
    ...otherProps
  } = props;

  const menuItems = useMemo(() => {
    const items = Array.isArray(children) ? [...children] : [children];
    if (placeholder) {
      items.unshift(
        <MenuItem key="placeholder" value="" disabled={!allowEmpty} sx={{ display: allowEmpty ? 'block' : 'none' }}>
          <span className="Mui-placeholder">{placeholder}</span>
        </MenuItem>,
      );
    }
    const values = items.map(({ props: { value } }) => value);

    if (showNotMapped && !multiple && !values.includes(value)) {
      items.push(
        <MenuItem key="notMappedValue" value={value as string} disabled={true} sx={{ display: 'none' }}>
          {value as string}
        </MenuItem>,
      );
    }
    return items;
  }, [children, placeholder, showNotMapped, multiple, value, allowEmpty]);
  const multipleValueRenderer = useMemo(() => renderMultipleValue<T>(placeholder, menuItems), [placeholder, menuItems]);

  const MenuPropsWithStyle = useMemo(
    () => ({ ...MenuProps, sx: size === 'medium' ? MenuSxPropMedium : MenuSxPropSmall }),
    [MenuProps, size],
  );

  return (
    <FormControl size={size} fullWidth={fullWidth} sx={sx}>
      {label && <InputLabel id="select-label">{label}</InputLabel>}
      <Select
        {...otherProps}
        labelId="select-label"
        multiple={multiple}
        renderValue={multiple ? multipleValueRenderer : renderValue}
        displayEmpty={displayEmpty}
        value={value}
        error={error}
        IconComponent={KeyboardArrowDownIcon}
        MenuProps={MenuPropsWithStyle}
        sx={selectBoxStyles}
      >
        {menuItems}
      </Select>
      {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </FormControl>
  );
};
